import { useCallback, useState } from 'react';
import { requireLocalCapacityFor, requireCloudSync } from '../../plans/localGate';
import { locationActionFor } from '../../plans/cloudAccess';
import { randomGradient, newId, relabelTaskManagerBoardTab } from './boardHelpers';
import { deviceScopeId, cloudIsFullNow, announceDeviceItem } from '../../records/itemLocation';
import * as boardsStore from '../../extensions/bundled/task-manager/boardsStore';
import { copyBoardToDevice, moveBoardToCloud, moveBoardToDevice } from '../../records/itemMove';
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { isWebApp } from '../../platform/target';
import { useAuthStore } from '../../store/authStore';
import teamsService from '../../services/teamsService';
import { setTeamLibraryRefresh } from '../../store/teamsStore';
import { noteBoardEdit } from '../../extensions/bundled/task-manager/boardChangeLog';
// Shared with the documents extension deliberately, not duplicated: the
// attachment outbox consults isDocDeleted() and nothing else, so marking ids
// there is the ONLY way a deleted task stops its queued uploads. The guard is
// keyed by id alone and both features use crypto.randomUUID(), so a collision
// would need two different uuids to be equal.
import { markDocsDeleted } from '../../extensions/bundled/documents/deletedDocsGuard';
import { closeTaskManagerBoardTab } from '../../store/tabsStore';
import { unpinBoard } from '../../extensions/bundled/task-manager/boardPins';
import { getProjectScope } from '../../scope/projectScopeStore';
import { addNewItemToActiveProject } from '../../scope/projectItems';
import { boardNodeId, boardTeamForProject, boardWireScope } from './boardProjectScope';
import { prefixChange } from '../../extensions/bundled/task-manager/taskKeyPrefixEdit';
import {
  editModalFromBoard,
  withLoadedBoard,
  labelsForSave,
  showDoneForSave,
  showColumnProgressForSave,
  closeFromCommitsForSave,
  deleteTargetFromModal,
} from '../../extensions/bundled/task-manager/boardEditModalState';

/**
 * Board-level actions: the create / edit modal, delete, and moving a board
 * between the cloud and this device. Moved out of TaskBoardTab unchanged.
 */
export default function useBoardAdmin({
  boardCacheRef,
  boardId,
  boardModal,
  boardRef,
  deleteBoardTarget,
  deletedIdsRef,
  dirtyEntityIdsRef,
  dirtyRef,
  loadBoards,
  loadedBoardIdRef,
  markDirtyEntity,
  persistRef,
  recomputeSaveState,
  setBoard,
  setBoardModal,
  setDeleteBoardTarget,
  shareableTeams,
}) {
  const openCreateBoardModal = useCallback(() => {
    // A team project starts the dialog on its own team (project scope 3.1).
    const projectTeam = boardTeamForProject(getProjectScope().activeProject, shareableTeams);
    setBoardModal({
      mode: 'create',
      name: '',
      description: '',
      color: randomGradient(),
      showDoneTasks: true,
      showColumnProgress: true,
      keyPrefix: '',
      // New boards close tasks from landed commits ("Fixes WEB-4"), like Linear / GitHub.
      closeFromCommits: true,
      visibility: projectTeam ? 'team' : 'private',
      teamId: projectTeam || (shareableTeams[0]?.id != null ? String(shareableTeams[0].id) : ''),
    });
  }, [setBoardModal, shareableTeams]);

  // The edit modal (Boards Redesign list 5). From the LOADED board when one is
  // in memory — summaries carry name and counts, not the palette, so seeding
  // from a summary would open the editor empty and saving would wipe every
  // label on the board. A board not in memory is read in the background; until
  // then the label editor says it is loading and a save keeps the board's own
  // palette (boardEditModalState.js).
  const openEditBoardModal = useCallback((b) => {
    const full =
      (boardRef.current?.id === b.id ? boardRef.current : null) ||
      dirtyRef.current.get(b.id) ||
      boardCacheRef.current.get(b.id) ||
      null;
    setBoardModal(editModalFromBoard(b, full, { gradient: randomGradient, scopeKeyOf: boardsStore.boardScopeKey }));
    if (full) return;
    boardsStore
      .readBoard(b.id, b.scopeKey)
      .then((read) => {
        if (read) setBoardModal((m) => withLoadedBoard(m, read));
      })
      .catch(() => {});
  }, [boardCacheRef, boardRef, dirtyRef, setBoardModal]);

  // "Delete board" in the modal's footer: close the modal and hand over to the
  // same confirm the tile's Delete opens — nothing is deleted without it.
  const requestDeleteFromModal = useCallback(() => {
    const target = deleteTargetFromModal(boardModal);
    if (!target) return;
    setBoardModal(null);
    setDeleteBoardTarget(target);
  }, [boardModal, setBoardModal, setDeleteBoardTarget]);

  // Cloud <-> this device (card C6). All or nothing on the server side, so a
  // refusal — the Free cloud-board limit — changes nothing and is reported by
  // the titlebar limits panel rather than swallowed here.
  const [movingBoardId, setMovingBoardId] = useState(null);
  const [cloudFull, setCloudFull] = useState(null);
  // Desktop, signed in — the only place a "this device" scope exists. The web
  // app holds nothing locally (records/web/webRecordsBackend is online-only by
  // owner decision), so offering to move a board there would promise storage
  // that does not exist; the command itself refuses with `desktop_only`.
  const canMoveLocation = Boolean(deviceScopeId());
  const moveBoardLocation = useCallback(async (board) => {
    if (!board?.id || movingBoardId) return;
    // Plans v2 card 3.4: by capability — see plans/cloudAccess.locationActionFor.
    const action = locationActionFor(board.location === boardsStore.LOCATION_DEVICE ? 'device' : 'cloud');
    if (action === 'upgrade') {
      requireCloudSync();
      return;
    }
    setMovingBoardId(board.id);
    try {
      const result =
        action === 'toCloud'
          ? await moveBoardToCloud(board.id)
          : action === 'download'
            ? await copyBoardToDevice(board.id)
            : await moveBoardToDevice(board.id);
      if (!result?.refused) notifyMcpSurfaceChanged('boards');
    } catch (err) {
      console.warn('[task-manager] moving the board failed:', err);
    } finally {
      setMovingBoardId(null);
      // The assembled board in the cache still carries the OLD location, and
      // the cache is authoritative when a tab reopens. Left stale, the next
      // edit would write the whole board back into the scope it just left —
      // re-creating the cloud copy of a board the user took offline.
      boardCacheRef.current.delete(board.id);
      dirtyRef.current.delete(board.id);
      if (loadedBoardIdRef.current === board.id) loadedBoardIdRef.current = null;
      loadBoards();
    }
  }, [boardCacheRef, dirtyRef, loadBoards, loadedBoardIdRef, movingBoardId]);

  const submitBoardModal = useCallback(async ({ takenPrefixes = [] } = {}) => {
    if (!boardModal) return;
    const { mode, id, name, description, color, showDoneTasks, showColumnProgress, visibility, teamId, labels } = boardModal;
    const trimmed = name.trim();
    if (!trimmed) return;
    const isTeam = visibility === 'team' && teamId;
    // Web only, and only for a PRIVATE board: nothing is kept locally here, so
    // a board past the personal cloud limit would be refused by the server and
    // lost on the next reload. A TEAM board is not governed by this limit at
    // all, which is why the check waits until the user has chosen — stopping
    // at "New board" locked a Free member of a paying team out of team boards
    // entirely. The desktop app has somewhere to put it and never stops here.
    if (mode === 'create' && !isTeam && isWebApp() && (await cloudIsFullNow('boards'))) {
      setBoardModal(null);
      setCloudFull('boards');
      return;
    }
    // Plans v2: Free makes 1 board of its own (team boards don't count). A full
    // plan opens the upgrade instead of creating.
    if (mode === 'create' && !isTeam) {
      const allowed = await requireLocalCapacityFor('boards', async () => (await boardsStore.countOwnBoardsAndTasks()).boards);
      if (!allowed) {
        setBoardModal(null);
        return;
      }
    }
    if (mode === 'create') {
      const newBoardId = newId();
      const now = new Date().toISOString();
      const newBoard = {
        id: newBoardId,
        name: trimmed,
        description: description.trim(),
        color,
        showDoneTasks: showDoneTasks !== false,
        showColumnProgress: showColumnProgress !== false,
        labels: labels || [],
        closeFromCommits: boardModal.closeFromCommits !== false,
        closeFromCommitsSince: boardModal.closeFromCommits !== false ? now : null,
        // A prefix typed into the new board's form (else useBoardTaskKeysFill derives one).
        ...(prefixChange({}, boardModal.keyPrefix, takenPrefixes).patch || {}),
        visibility: isTeam ? 'team' : 'private',
        teamId: isTeam ? String(teamId) : null,
        // Free plan: a private board goes to the cloud while there is room for
        // one there, and to this device once there is not (card C5).
        location: isTeam ? undefined : boardsStore.locationForNewBoard(),
        // The overview tile's owner avatar (extras §2.8).
        createdBy: useAuthStore.getState?.().user?.id != null ? String(useAuthStore.getState().user.id) : null,
        createdAt: now,
        updatedAt: now,
        groups: [],
        tasks: [],
      };
      await boardsStore.writeBoard(newBoard); // routes to team:<id> or personal scope
      notifyMcpSurfaceChanged('boards');
      // Owner decision 0.2: a board made while a project is active belongs to it.
      addNewItemToActiveProject(boardNodeId(newBoard), boardWireScope(newBoard));
      // Announced only now that the board exists, and the usage counts this
      // routing reads are refreshed here because nothing else refreshes them
      // after a create — stale counts put the next boards in the wrong place.
      if (newBoard.location === boardsStore.LOCATION_DEVICE) announceDeviceItem('boards');
      useAuthStore.getState?.().fetchUsage?.();
      if (isTeam) {
        // Record ownership so the Team page list + cross-device discovery see it.
        // Board content itself rides the team sync push regardless of this call.
        try {
          await teamsService.shareBoardWithTeam(newBoardId, { team_id: Number(teamId) || teamId });
        } catch (err) {
          console.warn('[task-manager] shareBoardWithTeam failed (board still synced):', err);
        }
        setTeamLibraryRefresh({ teamId: Number(teamId) || teamId, ts: Date.now() });
      }
      setBoardModal(null);
      loadBoards();
    } else {
      // Base the metadata edit on the authoritative in-memory board when we have
      // it (it may hold kanban edits not yet on disk); fall back to disk for a
      // board not opened this session. Routing the result through the dirty
      // queue (not a bare writeBoard) means it's retried on failure and never
      // clobbers pending edits.
      const base =
        dirtyRef.current.get(id) ||
        boardCacheRef.current.get(id) ||
        (await boardsStore.readBoard(id).catch(() => null));
      if (!base) {
        setBoardModal(null);
        return;
      }
      // A renamed task-key prefix keeps the old one as an alias (taskKeyPrefixEdit.js).
      const prefix = prefixChange(base, boardModal.keyPrefix, takenPrefixes);
      if (!prefix.ok) return; // the field shows why; the modal stays open
      const updated = {
        ...base,
        ...(prefix.patch || {}),
        ...closeFromCommitsForSave(boardModal, base),
        name: trimmed,
        description: description.trim(),
        color,
        showDoneTasks: showDoneForSave(boardModal, base),
        showColumnProgress: showColumnProgressForSave(boardModal, base),
        labels: labelsForSave(labels, base),
        updatedAt: new Date().toISOString(),
      };
      noteBoardEdit(base, updated); // the save writes just these settings
      boardCacheRef.current.set(id, updated);
      dirtyRef.current.set(id, updated);
      markDirtyEntity(id, '__meta__');
      if (boardId === id) {
        boardRef.current = updated;
        setBoard(updated);
        recomputeSaveState();
      }
      setBoardModal(null);
      relabelTaskManagerBoardTab(id, trimmed);
      const pending = persistRef.current?.();
      if (pending) await pending;
      notifyMcpSurfaceChanged('boards');
      loadBoards();
    }
  }, [boardModal, setBoardModal, loadBoards, dirtyRef, boardCacheRef, markDirtyEntity, boardId, persistRef, boardRef, setBoard, recomputeSaveState]);

  const confirmDeleteBoard = useCallback(async () => {
    if (!deleteBoardTarget) return;
    const id = deleteBoardTarget.id;
    const { scopeKey, visibility, teamId } = deleteBoardTarget;
    setDeleteBoardTarget(null);
    // Mark deleted BEFORE awaiting so an in-flight persist() write for this board
    // re-deletes the file it may resurrect (see persist).
    deletedIdsRef.current.add(id);
    // Every task on it, too — deleting a board orphans its attachments just as
    // surely as deleting the tasks one by one would.
    const doomed = boardCacheRef.current.get(id) || (boardRef.current?.id === id ? boardRef.current : null);
    if (doomed?.tasks?.length) markDocsDeleted(doomed.tasks.map((t) => t.id));
    boardCacheRef.current.delete(id);
    dirtyRef.current.delete(id);
    dirtyEntityIdsRef.current.delete(id);
    await boardsStore.deleteBoard(id, scopeKey).catch(() => {});
    // Its pin is a separate personal record (boardPins.js); a no-op if unpinned.
    unpinBoard(id).catch(() => {});
    notifyMcpSurfaceChanged('boards');
    if (visibility === 'team' && teamId != null) {
      try {
        await teamsService.removeTeamBoard(teamId, id);
      } catch (err) {
        console.warn('[task-manager] removeTeamBoard failed (board still tombstoned):', err);
      }
      setTeamLibraryRefresh({ teamId: Number(teamId) || teamId, ts: Date.now() });
    }
    closeTaskManagerBoardTab(id);
    loadBoards();
  }, [boardCacheRef, boardRef, deleteBoardTarget, deletedIdsRef, dirtyEntityIdsRef, dirtyRef, loadBoards, setDeleteBoardTarget]);

  return { openCreateBoardModal, openEditBoardModal, requestDeleteFromModal, movingBoardId, cloudFull, setCloudFull, canMoveLocation, moveBoardLocation, submitBoardModal, confirmDeleteBoard };
}
